import {
    AssignMobileOfferRequest,
    MobileOfferAssignment,
    MobileOfferAssignmentReadiness,
    MobileOfferAssignmentReadinessRequest,
    MobileOfferCatalogEntry,
    MobileOfferCatalogQuery,
    MobileOfferCapabilities,
    MobileOfferCustomer,
    MobileOfferPage,
    MobileOfferPreview,
    MobileOfferProvider,
    MobileOfferProvisioning,
    MobileOfferPurpose,
    MobileOfferTerms,
    MobilePromoCode,
    MobilePromoCodePlanOptions,
    MobilePromoCodeStatus,
    MobileRetentionPlanOffers,
    MobileRetentionStripePlan,
    ReassignMobileOfferReadinessRequest,
    ReassignMobileOfferRequest,
    UpsertMobilePromoCodeRequest,
} from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { AxiosResponse, isAxiosError } from 'axios'
import { mainServiceRootAxios } from '../../../../../setup/SetupAxios'
import { mobileOfferDebugOrigin, mobileOfferDebugRoute } from './diagnostics'

const base = '/v1/gluon/billing/mobile-offers'
interface Envelope<T> {
    success: boolean
    data: T
}
// TEMP MobileOffersDebug: retain for staging rollout. Never log Axios errors/bodies/tokens.
let debugSequence = 0
async function traceRequest<T>(method: string, path: string, action: () => Promise<AxiosResponse<Envelope<T>>>): Promise<T> {
    const backendOrigin = mobileOfferDebugOrigin(mainServiceRootAxios.defaults.baseURL, window.location.origin)
    if (!backendOrigin) return (await action()).data.data
    const requestId = ++debugSequence
    const startedAt = Date.now()
    console.info('[MobileOffersDebug] HTTP_STARTED', { requestId, method, route: `${base}${mobileOfferDebugRoute(path)}`, backendOrigin })
    try {
        const response = await action()
        console.info('[MobileOffersDebug] HTTP_COMPLETED', {
            requestId,
            status: response.status,
            success: response.data.success === true,
            backendOrigin,
            durationMs: Date.now() - startedAt,
        })
        return response.data.data
    } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined
        console.info('[MobileOffersDebug] HTTP_FAILED', {
            requestId,
            status: status ?? '(no HTTP response)',
            durationMs: Date.now() - startedAt,
            reason: status === 404 ? 'catalog_disabled_or_resource_missing' : status === 403 ? 'access_denied' : 'request_failed',
        })
        throw error
    }
}
async function get<T>(path: string, params?: Record<string, unknown>, timeout = 30_000): Promise<T> {
    return traceRequest('GET', path, () => mainServiceRootAxios.get<Envelope<T>>(`${base}${path}`, { params, timeout }))
}
async function post<T>(path: string, body: unknown): Promise<T> {
    return traceRequest('POST', path, () => mainServiceRootAxios.post<Envelope<T>>(`${base}${path}`, body, { timeout: 180_000 }))
}
async function put<T>(path: string, body: unknown): Promise<T> {
    return traceRequest('PUT', path, () => mainServiceRootAxios.put<Envelope<T>>(`${base}${path}`, body, { timeout: 60_000 }))
}
export const mobileOffersApi = {
    find: (id: string) => get<MobileOfferCatalogEntry>(`/catalog/${id}/v1`),
    list: (query?: MobileOfferCatalogQuery | string) =>
        get<MobileOfferPage<MobileOfferCatalogEntry>>('/catalog/v1', { limit: 20, ...(typeof query === 'string' ? { cursor: query } : query) }),
    capabilities: (plan: MobilePlan, purpose?: MobileOfferPurpose) =>
        get<MobileOfferCapabilities>('/capabilities/v1', { plan, ...(purpose ? { purpose } : {}) }, 180_000),
    create: (terms: MobileOfferTerms) => post<MobileOfferCatalogEntry>('/catalog/v1', terms),
    preview: (offerId: string, provider: MobileOfferProvider) => post<MobileOfferPreview>('/preview/v1', { offerId, provider }),
    provision: (preview: MobileOfferPreview) =>
        post<MobileOfferProvisioning>('/provision/v1', { offerId: preview.offerId, provider: preview.provider, confirmation: preview.confirmation }),
    status: (offerId: string, provider: MobileOfferProvider) => get<MobileOfferProvisioning | null>('/provision/status/v1', { offerId, provider }),
    codes: (offerId: string, requestId: string, expirationDate: string) =>
        post<{ status: string; numberOfCodes: number; resourceId: string }>('/apple/code-batches/v1', { offerId, requestId, expirationDate }),
    lookup: (identity: string) => get<MobileOfferCustomer[]>('/customers/lookup/v1', { identity }),
    customer: (userId: string) => get<MobileOfferCustomer>(`/customers/${encodeURIComponent(userId)}/v1`),
    assignmentReadiness: (request: MobileOfferAssignmentReadinessRequest) => get<MobileOfferAssignmentReadiness>('/assignments/readiness/v1', { ...request }),
    assign: (request: AssignMobileOfferRequest) => post<MobileOfferAssignment>('/assignments/v1', request),
    history: (userId: string, cursor?: string) => get<MobileOfferPage<MobileOfferAssignment>>(`/customers/${userId}/assignments/v1`, { cursor, limit: 20 }),
    reassignReadiness: (id: string, request: ReassignMobileOfferReadinessRequest) =>
        get<MobileOfferAssignmentReadiness>(`/assignments/${id}/reassign/readiness/v1`, { ...request }),
    reassign: (id: string, request: ReassignMobileOfferRequest) => post<MobileOfferAssignment>(`/assignments/${id}/reassign/v1`, request),
    retention: () => get<MobileRetentionPlanOffers[]>('/retention/v1'),
    /** Card (Stripe) row: per plan, the picked Promo Codes coupon and the coupons that could be picked. */
    stripeRetention: () => get<MobileRetentionStripePlan[]>('/retention/stripe/v1'),
    setStripeRetention: (plan: MobilePlan, couponId: string | null) => post<void>(`/retention/plans/${plan}/stripe/v1`, { couponId }),
    activateRetention: (id: string, store: MobileOfferProvider) => post<MobileOfferCatalogEntry>(`/retention/${id}/activate/v1`, { store }),
    generateRetentionCodes: (id: string) => post<{ status: string; numberOfCodes: number }>(`/retention/${id}/codes/v1`, {}),
    clearRetention: (plan: MobilePlan, store: MobileOfferProvider) =>
        post<{ plan: MobilePlan; store: MobileOfferProvider }>(`/retention/plans/${plan}/clear/v1`, { store }),
    revoke: (id: string, expectedRevision: number) => post<{ revoked: boolean }>(`/assignments/${id}/revoke/v1`, { expectedRevision }),
    /** Codes tab: shareable promo codes, each mapped per plan to an iOS offer / Android offer / card coupon. */
    promoCodes: () => get<MobilePromoCode[]>('/promo-codes/v1'),
    /** What the code form can pick per plan: iOS offers with their code pools, Android offers, card coupons. */
    promoCodeOptions: () => get<MobilePromoCodePlanOptions[]>('/promo-codes/options/v1'),
    /** Fill (or top up) a promo offer's App Store code pool — same batch as the Retention tab's "Generate codes". */
    generateAppleCodes: (id: string) => post<{ status: string; numberOfCodes: number }>(`/catalog/${id}/apple-codes/v1`, {}),
    createPromoCode: (request: UpsertMobilePromoCodeRequest) => post<MobilePromoCode>('/promo-codes/v1', request),
    updatePromoCode: (code: string, request: UpsertMobilePromoCodeRequest) => put<MobilePromoCode>(`/promo-codes/${encodeURIComponent(code)}/v1`, request),
    setPromoCodeStatus: (code: string, status: MobilePromoCodeStatus) =>
        post<MobilePromoCode>(`/promo-codes/${encodeURIComponent(code)}/status/v1`, { status }),
}

export function mobileOfferError(error: unknown): string {
    if (isAxiosError(error)) {
        const data: unknown = error.response?.data
        if (error.response?.status === 404) return 'Mobile offers are not enabled for this operator, or the resource is unavailable.'
        if (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string') return data.message
    }
    return 'The operation could not be confirmed. Refresh its status before retrying.'
}
